import { useMemo } from 'react'

import { Box, Typography, CircularProgress, Stack } from '@mui/material'

import { calculateDeliveryWindow } from 'constants/TrackingMap'
import { BookingStatus } from 'generated/graphql'

interface DeliveryTimeEstimateProps {
  variant?: 'overlay' | 'inline'
  nextAddressEta?: string | null
  etaWindowMinutes?: number | null
  loading?: boolean
  addressType?: 'collection' | 'delivery'
  bookingStatus?: BookingStatus | null
}

const getContainerStyles = (variant: 'overlay' | 'inline') => {
  if (variant === 'inline') {
    return {
      backgroundColor: '#2c2c54',
      borderRadius: 2,
      px: 3,
      py: 2,
      minWidth: 350,
      maxWidth: '100%',
      mb: 2,
    }
  }

  return {
    position: 'absolute' as const,
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#2c2c54',
    borderRadius: 2,
    px: 3,
    py: 2,
    minWidth: 350,
    maxWidth: '95%',
    zIndex: 1000,
  }
}

const labelStyles = {
  color: 'white',
  fontSize: '13px',
  fontWeight: 400,
  display: 'block',
  mb: 1,
}

const timeRangeStyles = {
  color: 'white',
  fontSize: '24px',
  fontWeight: 700,
}

const unavailableTextStyles = {
  color: 'white',
  fontSize: '16px',
  fontWeight: 400,
  fontStyle: 'italic' as const,
}

const durationContainerStyles = {
  backgroundColor: 'white',
  borderRadius: 1,
  px: 1.5,
  py: 0.75,
  ml: { xs: 0, sm: 2 },
}

const durationTextStyles = {
  color: '#2c2c54',
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
}

const ETAValue = ({
  loading,
  timeRange,
  duration,
  bookingStatus,
}: {
  loading: boolean
  timeRange?: string
  duration?: string
  bookingStatus?: BookingStatus | null
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} sx={{ color: 'white' }} />
        <Typography variant='h6' sx={{ ...unavailableTextStyles, fontStyle: 'normal' }}>
          Calculating...
        </Typography>
      </Box>
    )
  }

  if (timeRange) {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'column', md: 'row' }}
        justifyContent='space-between'
        spacing={2}>
        <Typography variant='h5' sx={timeRangeStyles}>
          {timeRange}
        </Typography>

        {duration && (
          <Box sx={durationContainerStyles}>
            <Typography
              textAlign='center'
              alignContent='center'
              variant='body2'
              sx={durationTextStyles}>
              {duration}
            </Typography>
          </Box>
        )}
      </Stack>
    )
  }

  const preCollectionStatuses = [
    BookingStatus.Quotation,
    BookingStatus.Allocated,
    BookingStatus.Waiting,
    BookingStatus.EBooking,
    BookingStatus.PackageOnBoardOnWay,
    BookingStatus.PackageOnBoardArrival,
  ]
  const isPreCollectionStatus = bookingStatus && preCollectionStatuses.includes(bookingStatus)

  return (
    <Typography variant='h6' sx={unavailableTextStyles}>
      {isPreCollectionStatus
        ? 'Estimated Delivery Time cannot be calculated before Collection.'
        : 'An ETA is not available at this time.'}
    </Typography>
  )
}

export const DeliveryTimeEstimate = ({
  variant = 'overlay',
  nextAddressEta,
  etaWindowMinutes,
  loading = false,
  addressType,
  bookingStatus,
}: DeliveryTimeEstimateProps) => {
  const { timeRange, duration } = useMemo(() => {
    if (nextAddressEta != null && etaWindowMinutes != null) {
      const { timeRange, duration } = calculateDeliveryWindow(nextAddressEta, etaWindowMinutes)
      return { timeRange, duration }
    }

    return {
      timeRange: undefined,
      duration: undefined,
    }
  }, [nextAddressEta, etaWindowMinutes])

  return (
    <Box sx={getContainerStyles(variant)}>
      <Typography variant='caption' sx={labelStyles}>
        {addressType === 'collection' ? 'Estimated collection:' : 'Estimated delivery:'}
      </Typography>
      <ETAValue
        loading={loading}
        timeRange={timeRange}
        duration={duration}
        bookingStatus={bookingStatus}
      />
    </Box>
  )
}
